import { Component, Input, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { LifecycleState, LifecycleHistoryItem } from '../../models/file-vault.models';
import { LifecycleBadgeComponent } from '../lifecycle-badge/lifecycle-badge.component';

export type HistoryViewMode = 'timeline' | 'grid';

@Component({
  selector: 'fv-lifecycle-history',
  standalone: true,
  template: `
    <div class="lifecycle-history" [class]="'mode-' + viewMode">
      <!-- View Mode Toggle -->
      <div class="view-toggle" *ngIf="showViewToggle">
        <button class="toggle-btn" [class.active]="viewMode === 'timeline'" (click)="viewMode = 'timeline'">
          Timeline
        </button>
        <button class="toggle-btn" [class.active]="viewMode === 'grid'" (click)="viewMode = 'grid'">
          Grid
        </button>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">Loading history...</div>

      <!-- Empty State -->
      <div class="empty" *ngIf="!isLoading && history.length === 0">
        No lifecycle history available
      </div>

      <!-- Timeline View -->
      <div class="timeline" *ngIf="viewMode === 'timeline' && !isLoading && history.length > 0">
        <div class="timeline-item" *ngFor="let item of history; let first = first; let last = last">
          <div class="timeline-marker" [class.first]="first" [class.last]="last">
            <div class="marker-dot"></div>
            <div class="marker-line" *ngIf="!last"></div>
          </div>

          <div class="timeline-content">
            <div class="transition-header">
              <div class="state-change">
                <fv-lifecycle-badge [state]="item.fromState" [showIcon]="false" size="small"></fv-lifecycle-badge>
                <span class="arrow">→</span>
                <fv-lifecycle-badge [state]="item.toState" [showIcon]="true" size="small"></fv-lifecycle-badge>
              </div>
            </div>

            <div class="transition-meta">
              <span class="user">{{ item.transitionedBy }}</span>
              <span class="separator">•</span>
              <span class="date">{{ item.transitionedAt | date:'medium' }}</span>
            </div>

            <div class="notes" *ngIf="item.notes || item.reason">
              <span class="notes-label">{{ item.reason ? 'Reason' : 'Notes' }}:</span>
              {{ item.reason || item.notes }}
            </div>
          </div>
        </div>
      </div>

      <!-- Grid View -->
      <kendo-grid *ngIf="viewMode === 'grid' && !isLoading && history.length > 0"
                  [data]="gridData"
                  [pageSize]="pageSize"
                  [pageable]="history.length > pageSize"
                  [sortable]="true"
                  class="history-grid">
        <kendo-grid-column field="transitionedAt" title="Date" [width]="180">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.transitionedAt | date:'medium' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="fromState" title="From" [width]="130">
          <ng-template kendoGridCellTemplate let-dataItem>
            <fv-lifecycle-badge [state]="dataItem.fromState" [showIcon]="false" size="small"></fv-lifecycle-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="toState" title="To" [width]="130">
          <ng-template kendoGridCellTemplate let-dataItem>
            <fv-lifecycle-badge [state]="dataItem.toState" [showIcon]="true" size="small"></fv-lifecycle-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="transitionedBy" title="By" [width]="150">
        </kendo-grid-column>

        <kendo-grid-column field="notes" title="Notes">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.reason || dataItem.notes || '-' }}
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>
    </div>
  `,
  styles: [`
    .lifecycle-history {
      padding: 8px 0;
    }

    .view-toggle {
      display: flex;
      gap: 4px;
      margin-bottom: 16px;
    }

    .toggle-btn {
      padding: 6px 12px;
      border: 1px solid var(--fv-border);
      background: var(--fv-surface);
      cursor: pointer;
      font-size: 12px;
      border-radius: 4px;
      transition: all 0.2s;
      color: var(--fv-text);
    }

    .toggle-btn:hover {
      background: var(--fv-surface-hover);
    }

    .toggle-btn.active {
      background: var(--fv-text-accent);
      color: white;
      border-color: var(--fv-text-accent);
    }

    .loading, .empty {
      padding: 24px;
      text-align: center;
      color: var(--fv-text-secondary);
      font-size: 14px;
    }

    /* Timeline Styles */
    .timeline {
      padding-left: 8px;
    }

    .timeline-item {
      display: flex;
      gap: 16px;
      padding-bottom: 24px;
    }

    .timeline-item:last-child {
      padding-bottom: 0;
    }

    .timeline-marker {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 20px;
      flex-shrink: 0;
    }

    .marker-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: var(--fv-text-accent);
      border: 2px solid var(--fv-surface);
      box-shadow: 0 0 0 2px var(--fv-text-accent);
      z-index: 1;
    }

    .timeline-item:first-child .marker-dot {
      background: var(--fv-status-success-fg);
      box-shadow: 0 0 0 2px var(--fv-status-success-fg);
    }

    .marker-line {
      flex: 1;
      width: 2px;
      background: var(--fv-border);
      margin-top: 4px;
    }

    .timeline-content {
      flex: 1;
      padding-bottom: 8px;
    }

    .transition-header {
      margin-bottom: 8px;
    }

    .state-change {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .arrow {
      color: var(--fv-text-secondary);
      font-size: 14px;
    }

    .transition-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: var(--fv-text-secondary);
      margin-bottom: 8px;
    }

    .separator {
      color: var(--fv-text-muted);
    }

    .user {
      font-weight: 500;
    }

    .notes {
      font-size: 13px;
      color: var(--fv-text);
      background: var(--fv-surface-alt);
      padding: 8px 12px;
      border-radius: 4px;
      border-left: 3px solid var(--fv-text-accent);
    }

    .notes-label {
      font-weight: 500;
      margin-right: 4px;
    }

    /* Grid Styles */
    .history-grid {
      font-size: 13px;
    }
  `],
  imports: [CommonModule, GridModule, LifecycleBadgeComponent]
})
export class LifecycleHistoryComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Entity type (Part, PartVersion, Document, DocumentVersion) */
  @Input() entityType!: string;

  /** Entity ID */
  @Input() entityId!: string;

  /** View mode: timeline or grid */
  @Input() viewMode: HistoryViewMode = 'timeline';

  /** Show view mode toggle */
  @Input() showViewToggle = true;

  /** Page size for grid view */
  @Input() pageSize = 10;

  /** Maximum items to show in timeline (0 = all) */
  @Input() maxTimelineItems = 0;

  history: LifecycleHistoryItem[] = [];
  isLoading = false;

  get gridData(): GridDataResult {
    return {
      data: this.history,
      total: this.history.length
    };
  }

  get displayHistory(): LifecycleHistoryItem[] {
    if (this.viewMode === 'timeline' && this.maxTimelineItems > 0) {
      return this.history.slice(0, this.maxTimelineItems);
    }
    return this.history;
  }

  ngOnInit(): void {
    this.loadHistory();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['entityType'] || changes['entityId']) && !changes['entityType']?.firstChange) {
      this.loadHistory();
    }
  }

  async loadHistory(): Promise<void> {
    if (!this.entityType || !this.entityId) return;

    this.isLoading = true;
    try {
      const history = await this.api.getLifecycleHistory(this.entityType, this.entityId).toPromise();
      // Sort by date descending (most recent first)
      this.history = (history ?? []).sort((a, b) =>
        new Date(b.transitionedAt).getTime() - new Date(a.transitionedAt).getTime()
      );
    } catch (error) {
      console.error('Failed to load lifecycle history', error);
      this.history = [];
    } finally {
      this.isLoading = false;
    }
  }

  /** Refresh the history */
  refresh(): void {
    this.loadHistory();
  }

  /** Get state label for display */
  getStateLabel(state: LifecycleState): string {
    const labels: Record<number, string> = {
      [LifecycleState.InWork]: 'In Work',
      [LifecycleState.InReview]: 'In Review',
      [LifecycleState.Approved]: 'Approved',
      [LifecycleState.Released]: 'Released',
      [LifecycleState.Superseded]: 'Superseded',
      [LifecycleState.Obsolete]: 'Obsolete'
    };
    return labels[state] ?? 'Unknown';
  }
}
